import { useEffect, useState } from 'react';
import {
    Alert, Box, FormControlLabel, Step, StepLabel, Stepper, Switch, TextField, Typography,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import MDButton from '@components/MDButton';
import MultiSelectStandalone from '@components/MultiSelectStandalone';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import {
    EIntegrationKind, Integration, IntegrationOption, IntegrationService,
} from '../services/integration.service';

interface Props {
    integration: Integration;
    onSaved: () => void;
    onClose: () => void;
    /// Dentro de um card de grupo a conexão já está no cabeçalho; repetir pareceria três conexões.
    hideConnection?: boolean;
}

/// Campos de uma integração, com assistente se o catálogo declara passos; também na aba do grupo.
export const IntegrationFields = ({
    integration, onSaved, onClose, hideConnection = false,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [values, setValues] = useState<Record<string, string>>({});
    const [active, setActive] = useState(false);
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState(0);

    /// Opções por fonte; ausente é "ainda buscando"; o erro vem do servidor, que sabe o motivo.
    const [optionLists, setOptionLists] = useState<
        Record<string, { options: IntegrationOption[]; error?: string }>
    >({});

    useEffect(() => {
        setActive(integration.enabled);
        setValues(
            Object.fromEntries(integration.fields.map((field) => [field.key, field.value]))
        );
        setOptionLists({});
        setStep(0);
    }, [integration]);

    /// Campo que depende de outro recarrega quando ele muda: ver as caixas da conta antiga seria pior.
    const dependencies = integration.fields
        .filter((field) => field.optionsSource)
        .map((field) => `${field.optionsSource}:${
            field.optionsDependsOn ? values[field.optionsDependsOn] ?? '' : ''}`)
        .join('|');

    useEffect(() => {
        integration.fields
            .filter((field) => field.optionsSource)
            .forEach((field) => {
                const fontSize = field.optionsSource!;
                const from = field.optionsDependsOn
                    ? values[field.optionsDependsOn]
                    : undefined;

                // Falhar não trava a tela: o campo mostra o motivo e o resto segue editável.
                new IntegrationService()
                    .options(fontSize, from || undefined)
                    .then((res) => setOptionLists((current) => ({
                        ...current, [fontSize]: { options: res.data.data ?? [] },
                    })))
                    .catch((err) => setOptionLists((current) => ({
                        ...current, [fontSize]: { options: [], error: apiErrorMessage(err) },
                    })));
            });
    }, [integration, dependencies]);

    const ehOAuth = integration.kind === EIntegrationKind.OAuth;
    // A Meta conecta as três integrações de uma vez, pelo controller dela.
    const isMeta = integration.groupKey === 'meta';
    const connected = Boolean(integration.accountLabel);
    // Sem conta conectada, ligar deixaria a integração ativa e sem acesso.
    const waitingConnection = ehOAuth && !isMeta && !connected;

    const steps = integration.steps ?? [];
    const hasSteps = steps.length > 0;
    const currentStep = steps[Math.min(step, steps.length - 1)];

    // Já configurada: os passos viram atalho; o assistente inteiro para trocar uma caixa seria pior.
    const canSkip = integration.configured;

    const stepFields = hasSteps
        ? integration.fields.filter((field) => field.step === currentStep?.key)
        : integration.fields;

    const lastStep = !hasSteps || step >= steps.length - 1;

    const save = async () => {
        setSaving(true);

        try {
            await new IntegrationService().save(integration.key, active, values);
            showAlert(t('general.atualizacao'), 'success', 2500);
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    const connect = async () => {
        try {
            const service = new IntegrationService();
            const res = isMeta ? await service.metaConnectUrl() : await service.accountConnectUrl(integration.key);

            if (!res.data.data.ready) {
                showAlert(
                    res.data.data.reason ?? t('integracoes.naoConfiguravel'), 'warning', 6000);
                return;
            }

            window.open(res.data.data.url!, '_blank', 'noopener');
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    return (
        <>
            {hasSteps && (
                <>
                    <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                        {steps.map((item, index) => (
                            <Step key={item.key} completed={canSkip && index !== step}>
                                <StepLabel
                                    onClick={canSkip ? () => setStep(index) : undefined}
                                    sx={canSkip ? { cursor: 'pointer' } : undefined}
                                >
                                    {item.title}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Typography
                        variant="body2"
                        sx={{ color: cc.textSecondary, mb: 2.5, lineHeight: 1.6 }}
                    >
                        {currentStep?.description}
                    </Typography>
                </>
            )}

            <Box sx={{ display: 'grid', gap: 2 }}>
                {stepFields.map((item) => {
                    // Campo com lista vira seletor, desabilitado até as opções chegarem: evita id colado à mão.
                    if (item.optionsSource) {
                        const list = optionLists[item.optionsSource];
                        const options = list?.options;
                        const current = values[item.key] ?? '';

                        const help = list === undefined
                            ? t('integracoes.carregandoOpcoes')
                            : list.error
                            ?? (options!.length === 0
                                ? t('integracoes.semOpcoes')
                                : item.help ?? undefined);

                        if (item.optionsMultiple) {
                            const selected = current
                                .split(',')
                                .map((value) => value.trim())
                                .filter((value) => value.length > 0);

                            return (
                                <LabelledSelect key={item.key} cc={cc} label={item.label}>
                                    {/* O campo guarda "2,3": a seleção múltipla é só a tela. */}
                                    <MultiSelectStandalone
                                        options={options ?? []}
                                        value={selected}
                                        disabled={!options || options.length === 0}
                                        onChange={(chosen) => setValues((previous) => ({
                                            ...previous,
                                            [item.key]: chosen.join(','),
                                        }))}
                                    />
                                    {help && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: cc.textMuted, display: 'block', mt: 0.5 }}
                                        >
                                            {help}
                                        </Typography>
                                    )}
                                </LabelledSelect>
                            );
                        }

                        const known = (options ?? []).some((o) => o.value === current);

                        return (
                            <LabelledSelect key={item.key} cc={cc} label={item.label}>
                                {/* O valor guardado entra na lista mesmo sem ela trazê-lo: senão o seletor viria vazio. */}
                                <StandaloneAutoCompleteSelect
                                    options={[
                                        ...(!known && current.length > 0
                                            ? [{ value: current, label: current }]
                                            : []),
                                        ...(options ?? []),
                                    ]}
                                    value={current}
                                    disabled={!options || options.length === 0}
                                    onChange={(value) => setValues((previous) => ({
                                        ...previous, [item.key]: (value as string) ?? '',
                                    }))}
                                />
                                {help && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: cc.textMuted, display: 'block', mt: 0.5 }}
                                    >
                                        {help}
                                    </Typography>
                                )}
                            </LabelledSelect>
                        );
                    }

                    return (
                        <LabelledSelect key={item.key} cc={cc} label={item.label}>
                            <TextField
                                size="small"
                                fullWidth
                                required={item.required}
                                placeholder={item.placeholder ?? undefined}
                                helperText={item.help ?? undefined}
                                type={item.secret ? 'password' : 'text'}
                                value={values[item.key] ?? ''}
                                onChange={(e) => setValues((current) => ({
                                    ...current, [item.key]: e.target.value,
                                }))}
                                autoComplete="off"
                            />
                        </LabelledSelect>
                    );
                })}
            </Box>

            {ehOAuth && !hideConnection && (isMeta || connected || integration.canConnect) && (
                <Box sx={{ mt: 2.5 }}>
                    {integration.accountLabel ? (
                        <Alert severity="success" icon={false} sx={{ mb: 1.5 }}>
                            <Typography variant="caption">
                                {t('integracoes.contaConectada', {
                                    conta: integration.accountLabel,
                                })}
                            </Typography>
                        </Alert>
                    ) : (
                        <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
                            <Typography variant="caption">
                                {isMeta
                                    ? t('integracoes.conecteAjuda')
                                    : t('integracoes.conecteContaAjuda', { nome: integration.name })}
                            </Typography>
                        </Alert>
                    )}

                    {/* Conectada sem o aplicativo no servidor: vale, mas reconectar não levaria a lugar nenhum. */}
                    {(isMeta || integration.canConnect) && (
                        <MDButton
                            variant="gradient"
                            color="info"
                            startIcon={isMeta ? <FacebookIcon /> : <LinkIcon />}
                            onClick={connect}
                            fullWidth
                        >
                            {connected
                                ? t('integracoes.reconectar')
                                : isMeta
                                    ? t('integracoes.conectarFacebook')
                                    : t('integracoes.conectarCom', { nome: integration.name })}
                        </MDButton>
                    )}
                </Box>
            )}

            {/* As credenciais moram no servidor (a mgcode as põe); o cliente só vê se já pode ligar. */}
            {lastStep && !integration.serverConfigured && (
                <Alert severity="warning" icon={false} sx={{ mt: 2.5 }}>
                    <Typography variant="caption">{t('integracoes.faltaServidor')}</Typography>
                </Alert>
            )}

            {lastStep && integration.serverConfigured && integration.fields.length === 0 && !ehOAuth && (
                <Alert severity="success" icon={false} sx={{ mt: 2.5 }}>
                    <Typography variant="caption">{t('integracoes.prontaNoServidor')}</Typography>
                </Alert>
            )}

            {/* Ligar a integração é decisão do fim, não de cada passo. */}
            {lastStep && (
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={(
                        <Switch
                            checked={active}
                            disabled={!active && (!integration.serverConfigured || waitingConnection)}
                            onChange={(e) => setActive(e.target.checked)}
                            color="success"
                        />
                    )}
                    label={(
                        <Typography variant="body2" sx={{ color: cc.textPrimary }}>
                            {t('integracoes.ativa')}
                        </Typography>
                    )}
                />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
                <MDButton variant="gradient" color="dark" onClick={onClose} disabled={saving}>
                    {t('general.fechar')}
                </MDButton>

                {hasSteps && step > 0 && (
                    <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={() => setStep(step - 1)}
                        disabled={saving}
                    >
                        {t('integracoes.voltar')}
                    </MDButton>
                )}

                {/* Salvar no meio do assistente gravaria metade da configuração como pronta. */}
                {lastStep ? (
                    <MDButton
                        variant="gradient"
                        color="success"
                        onClick={save}
                        disabled={saving}
                    >
                        {t('general.salvar')}
                    </MDButton>
                ) : (
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={() => setStep(step + 1)}
                        disabled={saving}
                    >
                        {t('integracoes.avancar')}
                    </MDButton>
                )}
            </Box>
        </>
    );
};
